<template>
  <div class="row">
    <div class="col-10 offset-1 offset-lg-2 ">
      <div class="mb-5 pt-5">
        <h3 class="mb-3"> Please share your reason for leaving</h3>
        <radio tabindex="1"
          v-for="(cancelationReason, index) in cancelationReasons"
          v-model="reason"
          @keyup.enter.native="reason = index"
          :item="index"
          :key="index"
          :id-name="`radio-${index}`">
          {{ cancelationReason.inputLabel }}
        </radio>
      </div>
    </div>

    <div class="col-11 col-sm-8 offset-1 offset-lg-2">
      <div class="mb-5">
        <h3 class="mb-3">How likely are you to return?</h3>
        <ul class="cancelation-rating d-inline-flex">
          <li v-for="index in 10" :key="index" class="star-rating">
            <label class="star-rating-item"
              tabindex="1"
              @keyup.enter="likelihoodToReturn = index">
              <input type="radio"
                v-model="likelihoodToReturn"
                :value="index"
                class='star-rating_radio'>
              <i :class="likelihoodToReturn >= index ? 'fas fa-star' : 'fal fa-star' " />
              <span>{{ index }}</span> </label>
          </li>
        </ul>
      </div>
    </div>

    <div class="col-11 col-sm-8 offset-1 offset-lg-2">
      <div class="mb-6 d-flex flex-column">
        <h3 class="mb-0">Anything else you'd like to tell us?</h3>
        <span class="mb-3 gray">Optional</span>
        <textarea v-model="comments" tabindex="1"
          class="w-100 pl-2 cancelation-textarea"
          rows="3" />
      </div>
    </div>

    <div class="col-11 col-sm-9 offset-1 offset-lg-2">
      <div class="mb-3 cancelation-checkbox">
        <checkbox v-model="acceptTerms" @keyup.enter.native="acceptTerms = !acceptTerms" tabindex="1">
          I understand that by proceeding, I will lose access to my rank tracking, geogrid, and post data,
          as well as my entire account history when my paid period ends.
        </checkbox>
      </div>
    </div>

    <div class="col-8 offset-1 offset-lg-2">
      <div class="mb-6">
        <button tabindex="1"
          class="btn btn-primary mr-1 mb-2"
          @click.prevent="cancelCancelation()">
          Nevermind
        </button>
        <button
          tabindex="1"
          class="btn mb-2"
          :disabled="waiting"
          @click.prevent="finalizeCancelation()">
          Cancel my plan
        </button>
      </div>
    </div>

    <b-modal
      modal-class="cancelation-modal"
      v-for="(reas, key) in cancelationReasons"
      :key="reas.id"
      :id="modalId(key)"
      :title="reas.headline">
      <!-- eslint-disable vue/no-v-html -->
      <p class="my-4" v-html="reas.paragraph" />
      <!-- eslint-disable vue/no-v-html -->

      <template #modal-footer="{ ok }">
        <b-button size="sm" variant="primary" @click="okFunction(ok, key)">
          {{ reas.ctaText }}
        </b-button>
      </template>
    </b-modal>

    <b-modal
      title="Canceling your PayPal subscription"
      id="modal-paypal-cancelation-instructions"
      modal-class="cancelation-modal"
      :no-close-on-backdrop="true">
      <p class="my-4">
        To cancel your PayPal subscription please login to PayPal,
        navigate to <a href="https://www.paypal.com/myaccount/autopay/">recurrent subscriptions page</a>
        and unsubscribe from LocalViking.<br>
        See <a href="https://www.paypal.com/ca/smarthelp/article/what-is-an-automatic-payment-faq2254">PayPal help center</a> for detailed instructions.
      </p>

      <template #modal-footer="">
        <b-button size="sm" variant="primary" @click="finalizePaypalCancelation()">
          Ok
        </b-button>
      </template>
    </b-modal>

    <b-modal
      title="Leave feedback for us"
      id="modal-feedback"
      modal-class="cancelation-modal">
      <textarea class="w-100 form-input" rows="8" v-model="feedback" />

      <template #modal-footer="">
        <b-button size="sm" variant="primary" @click="leaveFeedback()">
          Leave feedback
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>

import axios from 'axios'
import toastr from 'toastr'
import Vue from 'vue'
import { ModalPlugin } from 'bootstrap-vue'
import cancelationReasons from './cancelation_reasons'
import checkbox from '../components/checkbox'
import radio from '../components/radio'

Vue.use(ModalPlugin)

const FEEDBACK_SUCCESS_MSG = 'Thank you for your feedback.'
const FEEDBACK_ERROR_MSG = 'Please enter your feedback.'
const SUBMIT_ERROR_MSG = 'Please fill in all required fields.'

function makeCancelationRequest(cancelationId, reason, likelihoodToReturn, comment, paypalUser) {
  const url = `/subscription_cancelations/${cancelationId}/finalize`
  const payload = {
    reason: reason,
    likelihoodToReturn: likelihoodToReturn,
    comment: comment,
    paypalUser: paypalUser
  }

  axios.post(url, payload)
    .then(() => {
      const main = document.getElementById('js-cancelation-main')
      const final = document.getElementById('js-cancelation-final')
      main.classList.toggle('d-none')
      final.classList.toggle('d-none')
    })
    .catch((error) => toastr.error(error))
}

export default {
  components: { checkbox, radio },
  props: {
    paypalUser: {
      type: Boolean,
      required: true
    },
    billingUrl: {
      type: String,
      required: true
    },
    subscriptionCancelationId: {
      type: String,
      required: true
    },
    scheduleGroupDemoUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      reason: null,
      comments: null,
      feedback: null,
      likelihoodToReturn: null,
      acceptTerms: false,
      showInputError: false,
      waiting: false
    }
  },
  created() {
    this.brand = Styxie.WLC.brand
    this.cancelationReasons = cancelationReasons(this.brand)
  },
  watch: {
    reason() {
      this.$bvModal.show(`modal-${this.reason}`)
    }
  },
  computed: {
    validation() {
      return (!(this.acceptTerms && this.likelihoodToReturn !== null && this.reason !== null))
    }
  },
  methods: {
    modalId(key) {
      return `modal-${key}`
    },
    cancelCancelation() {
      const url = `/subscription_cancelations/${this.subscriptionCancelationId}/cancel`

      axios.post(url)
        .then(window.location = '/billing/edit')
        .catch((error) => toastr.error(error))
    },
    finalizeCancelation() {
      // If the user is a PayPal subscriber, we'll just pop up a modal for now until @sk finishes the 1.5 year long paypal native integration
      // In the future this should work seamlessly to cancel a sub if its paypal or stripe
      this.waiting = true

      if (this.validation) {
        toastr.error(SUBMIT_ERROR_MSG)
        this.waiting = false
        return false
      }

      if (this.paypalUser) {
        this.$bvModal.show('modal-paypal-cancelation-instructions')
        return false
      }

      makeCancelationRequest(
        this.subscriptionCancelationId,
        this.reason,
        this.likelihoodToReturn,
        this.comments,
        this.paypalUser
      )

      return true
    },
    finalizePaypalCancelation() {
      makeCancelationRequest(
        this.subscriptionCancelationId,
        this.reason,
        this.likelihoodToReturn,
        this.comments,
        this.paypalUser
      )

      return true
    },
    okFunction(defaultOkEvent, reason) {
      switch (reason) {
        case 'covid':
        case 'cost':
        case 'bugs':
        case 'complicated':
        case 'closed':
        case 'functionality':
        case 'support':
          window.Intercom('show')
          defaultOkEvent()
          break
        case 'setup':
        case 'training':
        case 'unready':
          window.open(this.scheduleGroupDemoUrl)
          break
        case 'time':
          window.open('https://www.youtube.com/watch?v=zCQ3IVlLEPU')
          break
        default:
          defaultOkEvent()
      }
    },
    leaveFeedback() {
      if (this.feedback == null) {
        toastr.error(FEEDBACK_ERROR_MSG)
        return false
      }

      const url = `/subscription_cancelations/${this.subscriptionCancelationId}/feedback`
      const payload = { feedback: this.feedback }

      axios.post(url, payload)
        .then(() => {
          this.$bvModal.hide('modal-feedback')
          toastr.success(FEEDBACK_SUCCESS_MSG)
          this.feedback = ''
        })
        .catch((error) => toastr.error(error))

      return true
    }
  }
}
</script>
